import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { Active, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { SortableOverlay } from './components/SortableOverlay';
import { StyleProps, UnorderedList } from '@chakra-ui/react';

interface SortableItemProps {
  id: UniqueIdentifier;
}

export interface SortableListProps<T extends SortableItemProps> {
  items: T[];
  onChange(items: T[]): void;
  renderItem(item: T, index?: number): ReactNode;
  style?: StyleProps;
}

export const SortableList = <T extends SortableItemProps>({
  items,
  onChange,
  renderItem,
  style: userStyle,
}: SortableListProps<T>): JSX.Element => {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(() => items.find((item) => item.id === active?.id), [active, items]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const style: StyleProps = {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    listStyleType: 'none',
    ml: 0,
    ...userStyle,
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }): void => {
        setActive(active);
      }}
      onDragEnd={({ active, over }): void => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);

          onChange(arrayMove(items, activeIndex, overIndex));
        }
        setActive(null);
      }}
      onDragCancel={(): void => setActive(null)}
    >
      <SortableContext items={items}>
        <UnorderedList {...style}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
          ))}
        </UnorderedList>
      </SortableContext>
      <SortableOverlay>{activeItem ? renderItem(activeItem) : null}</SortableOverlay>
    </DndContext>
  );
};
